<script setup lang="ts">
import type { TSite } from "@/data/api/kili/sites/types/sites.type";

import { useElementBounding } from "@vueuse/core";
import { computed, ref, useTemplateRef, watch } from "vue";

import { EZoomDirection, type IMapExpose, type IMapPoint } from "@/components/ui/map/map.types";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { useGetSitesQuery } from "@/composables/kili/sites/queries/use-get-sites-query";
import { useSearchFilter } from "@/composables/use-search-filter";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { cn } from "@/utils/libs/tailwind-utils";

import Message from "primevue/message";

import BaseLayout from "@/components/layout/base-layout.vue";
import PilotingSiteCardLeftPanel from "@/components/piloting/piloting-site-card-left-panel.vue";
import PilotingWizard from "@/components/piloting/wizard/piloting-wizard.vue";
import MapSiteMarker from "@/components/ui/map/map-site-marker.vue";
import MapZoomControls from "@/components/ui/map/map-zoom-controls.vue";
import Map from "@/components/ui/map/map.vue";

type TSiteMarker = { lat: number; lng: number } & TSite;

const { t, k } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const map = ref<IMapExpose>();
const { data: sites, isPending } = useGetSitesQuery();

const selectedSite = ref<TSite | undefined>();

const { searchInput, filteredList: filteredSites } = useSearchFilter(sites, "name");

const pilotingMapContainer = useTemplateRef("pilotingMapContainer");
const { left: pilotingMapLeft, width: pilotingMapWidth } = useElementBounding(pilotingMapContainer);
const pilotingSiteCardLeftPanelContainer = useTemplateRef("pilotingSiteCardLeftPanelContainer");
const { right: pilotingSiteCardLeftPanelRight, height: pilotingSiteCardLeftPanelContainerHeight } = useElementBounding(
  pilotingSiteCardLeftPanelContainer,
);
const leftPanelWhiteContainer = useTemplateRef("leftPanelWhiteContainer");
const { height: leftPanelWhiteContainerHeight } = useElementBounding(leftPanelWhiteContainer);
const isLeftPanelWhiteContainerHeightExceeding = ref<boolean>(false);

const centerCameraPadding = computed(() => {
  const leftOverlayWidth = pilotingSiteCardLeftPanelRight.value - pilotingMapLeft.value;
  return {
    left: Math.min(Math.max(leftOverlayWidth, 0), pilotingMapWidth.value),
  };
});

const isPilotingWizardVisible = computed<boolean>({
  get() {
    return selectedSite.value !== undefined;
  },
  set(value: boolean) {
    if (!value) {
      selectedSite.value = undefined;
    } else {
      // Only handling false value as we need to select a site before showing the wizard
      throw new Error("Do not set to true, instead, please set selectedSite's value");
    }
  },
});

async function handleSelectSite(site: TSite) {
  if (isEnvFeatureEnabled(newSectionManagementFeatureFlag)) {
    await router.push({ name: ERouteName.PLANNING, params: { siteId: site.siteId } });
    return;
  }

  selectedSite.value = site;
}

const markers = computed(() => {
  return (
    filteredSites.value?.map((site) => {
      return {
        lat: site.siteAddressDto.geolocatedSiteAddress.latitude,
        lng: site.siteAddressDto.geolocatedSiteAddress.longitude,
        ...site,
      };
    }) ?? ([] as Array<TSiteMarker>)
  );
});

const hasSiteProgramingSuspended = computed(() => {
  return (sites.value ?? []).some((site: TSite) => {
    return site.programingIsSuspended;
  });
});

function handleCenterMap(point: IMapPoint) {
  map.value?.centerCameraToPoint(point);
}

watch([pilotingSiteCardLeftPanelContainerHeight, leftPanelWhiteContainerHeight], ([newHeight, newThingHeight]) => {
  if (!newHeight || !newThingHeight) return;

  if (newThingHeight >= newHeight) {
    isLeftPanelWhiteContainerHeightExceeding.value = true;
  } else {
    isLeftPanelWhiteContainerHeightExceeding.value = false;
  }
});
</script>

<template>
  <BaseLayout
    :subtitle="t(k.control.piloting.subtitle)"
    :title="t(k.control.piloting.title)"
  >
    <div class="flex h-full w-full flex-col gap-4 pb-8">
      <Message
        v-if="hasSiteProgramingSuspended"
        class="m-1"
        :pt="{
          content: {
            class: 'bg-surface-warning p-1',
          },
        }"
        severity="warn"
      >
        <div class="w-full flex flex-row items-start justify-start gap-2">
          <i class="pi pi-info-circle w-fit pt-1" />

          <div class="w-full flex flex-col gap-2">
            <h4 class="text-base font-semibold flex flex-row items-center">
              {{ t(k.control.piloting.siteHasSuspendedProgramingWarning) }}
            </h4>
            <span class="text-amber-500">
              {{ t(k.control.piloting.siteHasSuspendedProgramingWarningDetails) }}
            </span>
          </div>
        </div>
      </Message>

      <div
        ref="pilotingMapContainer"
        class="flex flex-grow w-full"
      >
        <Map
          ref="map"
          :center-camera-padding="centerCameraPadding"
          :markers="markers"
        >
          <template #marker="{ marker, spiderfyOffset }">
            <MapSiteMarker
              data-test-id="piloting-map-site-marker"
              :site="marker"
              :spiderfy-offset="spiderfyOffset"
              @click:marker="handleCenterMap($event)"
              @click:programming="handleSelectSite($event)"
            />
          </template>
          <template #front>
            <MapZoomControls
              data-test-id="piloting-map-zoom-controls"
              :zoom-in-label="t(k.control.piloting.zoomIn)"
              :zoom-out-label="t(k.control.piloting.zoomOut)"
              @zoom-in="map?.zoom(EZoomDirection.IN)"
              @zoom-out="map?.zoom(EZoomDirection.OUT)"
            />

            <div
              ref="pilotingSiteCardLeftPanelContainer"
              class="absolute left-[24px] top-[24px] w-[360px] bottom-[24px]"
            >
              <div
                ref="leftPanelWhiteContainer"
                :class="
                  cn('pointer-events-auto shadow-lg w-full max-h-full p-4 bg-white rounded-xl', {
                    'h-full': isLeftPanelWhiteContainerHeightExceeding,
                  })
                "
              >
                <PilotingSiteCardLeftPanel
                  v-model:value="searchInput"
                  :is-pending="isPending"
                  :sites="filteredSites"
                  @click:site="handleSelectSite($event)"
                  @update:search-site="isLeftPanelWhiteContainerHeightExceeding = false"
                />
              </div>
            </div>
          </template>
        </Map>
      </div>
    </div>
  </BaseLayout>

  <PilotingWizard
    v-model:visible="isPilotingWizardVisible"
    :site="selectedSite"
  />
</template>
